import React from "react";
import { Link } from "react-router-dom";
import { Card, SectionLabel } from "../components/ui-bits";
import { ArrowLeft } from "lucide-react";
import { SeoHead } from "../seo/SeoHead";

/** Shared shell so legal pages fit inside the terminal aesthetic regardless of auth state. */
function LegalShell({ title, kicker, children, testId }) {
  return (
    <div className="min-h-screen bg-fij-bg text-fij-text" data-testid={testId}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14 space-y-8">
        <div>
          <Link
            to="/"
            data-testid="legal-back"
            className="inline-flex items-center gap-1.5 font-mono text-[11px] text-fij-textDim hover:text-fij-green transition-colors"
          >
            <ArrowLeft size={12} /> BACK TO TERMINAL
          </Link>
          <div className="mt-6">
            <SectionLabel>{kicker}</SectionLabel>
            <h1 className="fij-mono text-2xl sm:text-3xl text-fij-text mt-1">{title}</h1>
            <div className="font-mono text-[11px] text-fij-textMuted mt-1">
              Effective date · 5 July 2026 · Financial Impact Journal (FIJ)
            </div>
          </div>
        </div>

        <Card>
          <div className="prose-terminal space-y-6">
            {children}
          </div>
        </Card>

        <LegalFooter />
      </div>
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section>
      <h2 className="font-mono text-sm text-fij-green">
        <span className="text-fij-textMuted mr-2">{n}.</span>{title}
      </h2>
      <div className="mt-2 space-y-2 text-fij-textDim text-sm leading-relaxed">
        {children}
      </div>
    </section>
  );
}

export function LegalFooter() {
  return (
    <footer className="pt-4 border-t border-fij-border/60 flex flex-wrap items-center justify-between gap-3 font-mono text-[11px] text-fij-textMuted">
      <span>© 2026 Financial Impact Journal · All rights reserved.</span>
      <div className="flex items-center gap-4">
        <Link to="/privacy"  data-testid="footer-privacy" className="hover:text-fij-green">Privacy Policy</Link>
        <Link to="/terms"    data-testid="footer-terms"   className="hover:text-fij-green">Terms &amp; Conditions</Link>
        <Link to="/contact"  data-testid="footer-contact" className="hover:text-fij-green">Contact</Link>
      </div>
    </footer>
  );
}

/* ================== PRIVACY POLICY ================== */
export function PrivacyPage() {
  return (
    <LegalShell testId="privacy-page" kicker="LEGAL // PRIVACY_POLICY" title="Privacy Policy">
      <SeoHead path="/privacy" />
      <p>
        This Privacy Policy explains how Financial Impact Journal (&quot;FIJ&quot;, &quot;we&quot;, &quot;our&quot;) collects,
        stores, and uses your information when you use the FIJ terminal at{" "}
        <span className="text-fij-text">fij-dashboard.emergent.host</span>. By creating an account you
        acknowledge and accept the practices described here.
      </p>

      <Section n={1} title="User Authentication">
        <p>
          To use FIJ you must create an account. During sign-up we collect your full name, email
          address, and a password you choose. To finalize registration we generate a one-time
          8-character Access Key that only you see and must re-enter to confirm ownership of the
          session.
        </p>
        <p>
          Your password and access key are <span className="text-fij-text">never stored in plain text</span>.
          Both are hashed with bcrypt (12 rounds) before persistence. Sign-in is protected by
          short-lived JWT session tokens; logout invalidates them client-side.
        </p>
      </Section>

      <Section n={2} title="Data Storage in MongoDB Atlas">
        <p>
          Account data (user id, full name, email, bcrypt hashes for password and access key,
          creation timestamp, email-verified flag, last-login timestamp) is stored in a
          MongoDB Atlas cluster hosted in the cloud. Only the FIJ backend can read these records;
          no third party has direct access.
        </p>
        <p>
          Operational data — market prices, index snapshots, news items, AI analyses of stocks and
          news events, morning briefs, and daily analysis-quota counters — is stored in FIJ&apos;s
          internal MongoDB instance to power your dashboard experience. These records are
          keyed to your account only where necessary (e.g., daily AI-analysis quota).
        </p>
      </Section>

      <Section n={3} title="Cookies &amp; Session Usage">
        <p>
          FIJ does <span className="text-fij-text">not</span> use tracking cookies, advertising
          cookies, or third-party analytics beacons. To keep you signed in we store a signed JWT
          session token and a small profile object in your browser&apos;s <code className="text-fij-text">localStorage</code>.
          Signing out or clearing your browser storage removes these immediately.
        </p>
      </Section>

      <Section n={4} title="AI-Generated Content Disclaimer">
        <p>
          FIJ uses Claude Sonnet (via Anthropic) to generate: the Morning Brief, the Market
          Narrative synthesis, Financial Journal impact analyses, event-detail summaries, and
          per-stock AI analyses (summary, verdict, confidence, strengths, risks).
        </p>
        <p>
          AI outputs are probabilistic and may contain factual errors, outdated information, or
          reasoning gaps. They are <span className="text-fij-text">informational only</span> and
          must not be treated as personal financial advice, portfolio recommendations, or
          guarantees of any market outcome. Always verify against primary sources before acting.
        </p>
      </Section>

      <Section n={5} title="Third-Party APIs">
        <p>
          To provide live market data, news, and AI insights, FIJ makes server-side requests to
          the following third-party services:
        </p>
        <ul className="list-disc list-inside space-y-1 text-fij-text">
          <li>Yahoo Finance &amp; Twelve Data — index and stock price/history feeds.</li>
          <li>MarketAux, Finnhub, and Alpha Vantage — financial news headlines and metadata.</li>
          <li>Anthropic (Claude Sonnet) — AI text generation for briefs, analyses, and narratives.</li>
          <li>MongoDB Atlas — authentication data storage.</li>
        </ul>
        <p>
          These providers each have their own privacy policies. FIJ does not share your account
          identifiers with them — requests are made from FIJ&apos;s backend only.
        </p>
      </Section>

      <Section n={6} title="Your Rights &amp; Contact">
        <p>
          You may request deletion of your account and associated data at any time by reaching us
          via the <Link to="/contact" className="text-fij-green hover:underline">Contact page</Link>.
          We will action verified requests within 30 days.
        </p>
      </Section>
    </LegalShell>
  );
}

/* ================== TERMS & CONDITIONS ================== */
export function TermsPage() {
  return (
    <LegalShell testId="terms-page" kicker="LEGAL // TERMS_&amp;_CONDITIONS" title="Terms &amp; Conditions">
      <SeoHead path="/terms" />
      <p>
        These Terms govern your use of the Financial Impact Journal (&quot;FIJ&quot;) terminal. By creating
        an account or using any part of the service you agree to be bound by them.
      </p>

      <Section n={1} title="Informational Purpose Only">
        <p>
          FIJ is an <span className="text-fij-text">informational research terminal</span>. All
          content — including index prices, stock snapshots, AI-generated summaries, verdicts,
          strengths, risks, market narratives, and morning briefs — is provided for informational
          and educational purposes only.
        </p>
      </Section>

      <Section n={2} title="Not Investment Advice">
        <p>
          Nothing on FIJ constitutes investment advice, a recommendation to buy or sell any
          security, a solicitation, or an offer. FIJ is not a SEBI-registered investment adviser,
          research analyst, portfolio manager, or broker.
        </p>
      </Section>

      <Section n={3} title="Consult a Qualified Advisor">
        <p>
          Before making any investment decision you must consult a licensed financial adviser
          familiar with your personal financial situation, risk appetite, tax status, and
          objectives. Past performance shown on FIJ is not indicative of future results.
        </p>
      </Section>

      <Section n={4} title="AI Insights May Contain Inaccuracies">
        <p>
          FIJ uses large-language-model AI (Claude Sonnet) to generate insights. AI outputs may
          contain factual errors, out-of-date information, mis-summarized headlines, or biased
          reasoning. You are responsible for independently verifying any AI-generated statement
          before relying on it.
        </p>
      </Section>

      <Section n={5} title="Limitation of Liability">
        <p>
          FIJ, its authors, contributors, and infrastructure providers are{" "}
          <span className="text-fij-text">not responsible for any investment losses</span>,
          missed opportunities, tax consequences, or other damages — direct or indirect — arising
          from your use of, or reliance on, information presented on the terminal.
        </p>
        <p>
          The service is provided &quot;as is&quot; and &quot;as available&quot;, without warranties of any kind,
          express or implied, including merchantability, fitness for a particular purpose, or
          non-infringement.
        </p>
      </Section>

      <Section n={6} title="Acceptable Use">
        <p>
          You agree not to (a) misuse the API for automated scraping beyond ordinary interactive
          use, (b) attempt to bypass authentication or the daily AI-analysis quota, (c) attempt to
          reverse-engineer or interfere with the backend, or (d) use FIJ for any unlawful purpose.
        </p>
      </Section>

      <Section n={7} title="Account Termination">
        <p>
          We may suspend or terminate accounts that violate these Terms, at our discretion, with
          or without notice. You may terminate your account at any time via the{" "}
          <Link to="/contact" className="text-fij-green hover:underline">Contact page</Link>.
        </p>
      </Section>

      <Section n={8} title="Changes to These Terms">
        <p>
          FIJ may update these Terms as the product evolves. Material changes will be surfaced on
          the terminal. Continued use after a change constitutes acceptance of the revised Terms.
        </p>
      </Section>

      <Section n={9} title="Governing Law">
        <p>
          These Terms are governed by the laws of India. Any disputes shall be resolved in the
          courts of Mumbai, Maharashtra.
        </p>
      </Section>
    </LegalShell>
  );
}

/* ================== CONTACT ================== */
export function ContactPage() {
  return (
    <LegalShell testId="contact-page" kicker="LEGAL // CONTACT" title="Contact Us">
      <SeoHead path="/contact" />
      <p>
        Have a question, a bug report, a feature request, or a data-deletion request? We&apos;d love
        to hear from you.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
        <ContactCell label="General &amp; Support" value="support@fij.io" testid="contact-support" />
        <ContactCell label="Data / Privacy" value="privacy@fij.io" testid="contact-privacy" />
        <ContactCell label="Partnerships"    value="hello@fij.io"   testid="contact-partnership" />
        <ContactCell label="Response Time"   value="Within 2 business days" testid="contact-sla" plain />
      </div>

      <div className="pt-3 text-sm text-fij-textDim leading-relaxed">
        <p>
          Please include the email address associated with your FIJ account and, when reporting
          a bug, the page you were on and a short description of what you observed vs expected.
        </p>
        <p className="mt-2">
          For urgent security disclosures, contact{" "}
          <a href="mailto:security@fij.io" className="text-fij-green hover:underline">security@fij.io</a>{" "}
          — we treat these with priority.
        </p>
      </div>
    </LegalShell>
  );
}

function ContactCell({ label, value, plain, testid }) {
  return (
    <div className="border border-fij-border rounded-sm p-3">
      <div className="text-[10px] uppercase text-fij-textMuted font-mono">{label}</div>
      {plain
        ? <div className="font-mono text-sm text-fij-text mt-1" data-testid={testid}>{value}</div>
        : (
          <a
            href={`mailto:${value}`}
            data-testid={testid}
            className="font-mono text-sm text-fij-green hover:underline mt-1 block break-all"
          >{value}</a>
        )}
    </div>
  );
}
